import { apiClient, type ApiResponse } from './client';

// Scrap register: lots of scrap from production rejections, incoming rejections, process waste and
// write-offs; approved into the scrap yard, then sold / destroyed / donated.

export type ScrapSource = 'PRODUCTION' | 'GRN' | 'PROCESS' | 'WRITE_OFF';
export type ScrapStatus = 'PENDING' | 'APPROVED' | 'DISPOSED' | 'REJECTED';
export type ScrapReason =
  | 'DIMENSIONAL_ERROR' | 'SURFACE_DEFECT' | 'MATERIAL_DEFECT' | 'MACHINE_ERROR'
  | 'OPERATOR_ERROR' | 'EXPIRED' | 'OBSOLETE' | 'PROCESS_WASTE' | 'OTHER';
export type DisposalMethod = 'SOLD' | 'DESTROYED' | 'DONATED';

export const SCRAP_SOURCE_LABEL: Record<ScrapSource, string> = {
  PRODUCTION: 'Production rejection',
  GRN: 'Incoming (GRN) rejection',
  PROCESS: 'Process scrap',
  WRITE_OFF: 'Write-off',
};

export const SCRAP_REASON_LABEL: Record<ScrapReason, string> = {
  DIMENSIONAL_ERROR: 'Dimensional error',
  SURFACE_DEFECT: 'Surface defect',
  MATERIAL_DEFECT: 'Material defect',
  MACHINE_ERROR: 'Machine error',
  OPERATOR_ERROR: 'Operator error',
  EXPIRED: 'Expired',
  OBSOLETE: 'Obsolete',
  PROCESS_WASTE: 'Process waste',
  OTHER: 'Other',
};

export interface ScrapEntry {
  id: string;
  scrap_number: string;
  entry_date: string;
  source_type: ScrapSource;
  source_number: string | null;
  production_order_id: string | null;
  production_order_number: string | null;
  work_order_id: string | null;
  stage_number: number | null;
  stage_name: string | null;
  grn_id: string | null;
  grn_number: string | null;
  ncr_number: string | null;
  item_id: string;
  item_name: string;
  item_code?: string | null;
  warehouse_id: string | null;
  warehouse_name?: string | null;
  scrap_location: string;
  qty: string | number;
  unit: string | null;
  book_rate: string | number;
  book_value: string | number;
  scrap_rate: string | number;
  scrap_value: string | number;
  reason: ScrapReason;
  notes: string | null;
  deduct_stock: boolean;
  status: ScrapStatus;
  approved_by_name: string | null;
  approved_at: string | null;
  review_note: string | null;
  disposal_method: DisposalMethod | null;
  disposal_date: string | null;
  buyer_name: string | null;
  sale_amount: string | number;
  gst_rate: string | number;
  gst_type: 'INTRA' | 'INTER' | null;
  gst_amount: string | number;
  disposal_cost: string | number;
  payment_mode: 'CASH' | 'BANK' | null;
  disposal_reference: string | null;
  disposed_by_name: string | null;
  created_by_name: string | null;
  created_at: string;
  /** P&L loss of the entry so far (book − scrap value, then the disposal result) */
  loss: number;
  /** Sale proceeds (ex GST) */
  recovery: number;
  vouchers?: { id: string; voucher_number: string; voucher_date: string; voucher_type: string; total_debit: string; narration: string }[];
}

export interface ScrapSummary {
  pending: { count: number; bookValue: number; scrapValue: number };
  approved: { count: number; scrapValue: number };
  disposed: { count: number; saleAmount: number };
  rejected: { count: number };
  loss: number;
  recovery: number;
}

export interface ScrapStockRow {
  id: string;
  item_id: string;
  item_name: string;
  item_code: string | null;
  unit: string | null;
  warehouse_id: string | null;
  warehouse_name: string | null;
  scrap_location: string;
  qty: string | number;
  value: string | number;
  avg_scrap_rate: string | number;
  sources: string | null;
  open_lots: number;
  updated_at: string;
}

export interface StageRejection {
  stageNumber: number;
  stageName: string;
  workOrderId: string;
  rejectedQty: number;
  scrappedQty: number;
  pendingQty: number;
}

export interface ProductionOrderScrap {
  productionOrder: { id: string; po_number: string; completed_qty: string; rejected_qty: string; planned_qty: string };
  stages: StageRejection[];
  entries: ScrapEntry[];
  totals: { scrapValue: number; bookValue: number; disposedValue: number; pendingValue: number; recovery: number; loss: number };
}

export interface ScrapGroup {
  key: string;
  label: string;
  entries: number;
  qty: number;
  bookValue: number;
  scrapValue: number;
  loss: number;
  recovery: number;
  unit?: string;
  itemCode?: string;
  productName?: string | null;
  plannedQty?: number;
  goodQty?: number;
  rejectedQty?: number;
  yieldPct?: number | null;
  scrapPct?: number | null;
}

export interface ScrapReport {
  totals: { entries: number; bookValue: number; scrapValue: number; loss: number; recovery: number; gstOnSales: number; disposalCost: number; pendingValue: number };
  bySource: ScrapGroup[];
  byItem: ScrapGroup[];
  byOrder: ScrapGroup[];
  byStage: ScrapGroup[];
  byReason: ScrapGroup[];
  byMonth: ScrapGroup[];
  sales: { id: string; scrapNumber: string; disposalDate: string; buyerName: string; itemName: string; qty: number; unit: string; scrapValue: number; saleAmount: number; gstAmount: number; result: number }[];
}

export interface CreateScrapPayload {
  sourceType: ScrapSource;
  itemId?: string;
  qty: number;
  reason: ScrapReason;
  notes?: string;
  productionOrderId?: string;
  stageNumber?: number | null;
  grnId?: string;
  warehouseId?: string;
  scrapRate?: number | null;
  scrapLocation?: string;
  entryDate?: string;
  deductStock?: boolean;
}

export interface DisposePayload {
  method: DisposalMethod;
  disposalDate?: string;
  buyerName?: string;
  saleAmount?: number;
  gstRate?: number;
  gstType?: 'INTRA' | 'INTER';
  disposalCost?: number;
  paymentMode?: 'CASH' | 'BANK';
  reference?: string;
  notes?: string;
}

const BASE = '/api/v1/manufacturing/scrap';

/** Axios rejects on 4xx/5xx: surface the server's message. */
async function call<T>(p: Promise<{ data: ApiResponse<T> }>): Promise<ApiResponse<T>> {
  try {
    return (await p).data;
  } catch (err) {
    const res = (err as { response?: { data?: ApiResponse<T> } }).response?.data;
    throw new Error(res?.message || (err as Error).message || 'Request failed');
  }
}

const clean = (params: Record<string, string | undefined>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== 'ALL'));

export const scrapApi = {
  list: (params: { status?: string; source?: string; from?: string; to?: string; search?: string; warehouseId?: string; production_order_id?: string } = {}) =>
    call<ScrapEntry[]>(apiClient.get(BASE, { params: clean(params) })),
  summary: (params: { from?: string; to?: string; source?: string; warehouseId?: string } = {}) =>
    call<ScrapSummary>(apiClient.get(`${BASE}/summary`, { params: clean(params) })),
  get: (id: string) => call<ScrapEntry>(apiClient.get(`${BASE}/${id}`)),
  create: (payload: CreateScrapPayload) => call<ScrapEntry>(apiClient.post(BASE, payload)),
  approve: (id: string, body: { scrapRate?: number | null; scrapLocation?: string; note?: string }) =>
    call<ScrapEntry>(apiClient.post(`${BASE}/${id}/approve`, body)),
  reject: (id: string, note: string) => call<ScrapEntry>(apiClient.post(`${BASE}/${id}/reject`, { note })),
  dispose: (id: string, body: DisposePayload) => call<ScrapEntry>(apiClient.post(`${BASE}/${id}/dispose`, body)),
  myRights: () => call<{ canApprove: boolean }>(apiClient.get(`${BASE}/approval/me`)),
  stock: (warehouseId?: string) => call<ScrapStockRow[]>(apiClient.get(`${BASE}/stock`, { params: clean({ warehouseId }) })),
  forProductionOrder: (id: string) => call<ProductionOrderScrap>(apiClient.get(`${BASE}/production-order/${id}`)),
  report: (params: { from?: string; to?: string; warehouseId?: string; source?: string }) =>
    call<ScrapReport>(apiClient.get(`${BASE}/report`, { params: clean(params) })),
  productionOptions: (warehouseId?: string) =>
    call<ProductionScrapOption[]>(apiClient.get(`${BASE}/options`, { params: clean({ source: 'PRODUCTION', warehouseId }) })),
  grnOptions: (warehouseId?: string) =>
    call<GrnScrapOption[]>(apiClient.get(`${BASE}/options`, { params: clean({ source: 'GRN', warehouseId }) })),
  writeOffOptions: (warehouseId: string) =>
    call<WriteOffOption[]>(apiClient.get(`${BASE}/options`, { params: { source: 'WRITE_OFF', warehouseId } })),
};

/** Orders with rejected units not yet scrapped */
export interface ProductionScrapOption {
  id: string;
  po_number: string;
  status: string;
  product_name: string;
  scrap_rate: string | number;
  unit: string;
  stages: { stageNumber: number; stageName: string; rejectedQty: number; pendingQty: number }[];
}

export interface GrnScrapOption {
  id: string;
  grn_number: string;
  date: string;
  status: string;
  supplier_name: string | null;
  items: { itemId: string; itemName: string; unit: string; qty: number; rate: number; scrapRate: number; scrappedQty: number }[];
}

export interface WriteOffOption {
  id: string;
  name: string;
  code: string | null;
  item_type: string;
  unit: string;
  scrap_rate: string | number;
  stock: string | number;
  pending_write_off: string | number;
}

export const num = (v: string | number | null | undefined) => Number(v || 0);
